// Authenticated CSV download (BUILD_SPEC.md §13: every report is "CSV-exportable";
// services/api's reporting module attaches every report's `?format=csv` variant —
// see services/api/src/modules/reporting/csv.ts). A plain `<a href>` can't carry the
// `Authorization`/`X-Tenant-Id` headers the API requires (services/api's
// middleware/auth.ts contract) — the browser would just hit an unauthenticated 401.
//
// `src/api/client.ts`'s `apiFetch` can't be reused as-is either: it only parses
// `application/json` bodies (see its own header comment), so a `text/csv` response
// falls through its content-type check and the caller gets back `undefined`, not the
// CSV bytes. Rather than edit `client.ts` (out of this goal's S0 ownership — "Do NOT
// touch ... api/client base"), this hook is a small, self-contained sibling: it reads
// auth state from the same public `useAuth()` hook `client.ts`'s own `AuthProvider`
// populates, and duplicates `client.ts`'s base-URL fallback convention (same env var,
// same literal default) rather than importing it.
import { useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';

/** Mirrors src/api/client.ts's DEFAULT_BASE_URL exactly (services/api's local dev
 * server default port — services/api/src/index.ts). */
const DEFAULT_BASE_URL = 'http://localhost:3001/api/v1';

function baseUrl(): string {
  return import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL;
}

export type CsvQuery = Record<string, string | number | boolean | undefined>;

function buildCsvUrl(path: string, query: CsvQuery): string {
  const url = new URL(path.replace(/^\//, ''), `${baseUrl().replace(/\/$/, '')}/`);
  url.searchParams.set('format', 'csv');
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/** Extracts the server-chosen filename from `Content-Disposition: attachment;
 * filename="...";` falling back to a caller-supplied default if the header is
 * missing or unparsable for any reason. */
function filenameFromDisposition(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return match?.[1] ?? fallback;
}

/**
 * Returns a `download(path, query, fallbackFilename)` function that fetches a
 * report's CSV variant as a Blob (with the same bearer/tenant headers every other
 * request carries) and triggers a normal browser file-save via a temporary
 * object-URL anchor — the standard client-side pattern for authenticated binary
 * downloads that can't go through a bare `<a href>`.
 */
export function useCsvDownload() {
  const auth = useAuth();

  return useCallback(
    async (path: string, query: CsvQuery, fallbackFilename: string): Promise<void> => {
      const headers: Record<string, string> = { Accept: 'text/csv' };
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`;
      if (auth.activeTenant) headers['X-Tenant-Id'] = auth.activeTenant.tenantId;

      const response = await fetch(buildCsvUrl(path, query), { headers });
      if (!response.ok) {
        throw new Error(`CSV download failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const filename = filenameFromDisposition(response.headers.get('content-disposition'), fallbackFilename);

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },
    [auth.token, auth.activeTenant],
  );
}
